<template>
  <div
    v-show="showScrollButton"
    class="scroll-to-top-button critical-component-clear"
    :class="isStickyVisible ? 'scroll-to-top-button--above-sticky' : ''"
  >
    <btn
      class="scroll-to-top-button__button"
      show-icon
      type="button"
      size="medium"
      url="#main-content"
      modifiers="secondary"
      label="Scroll to top"
      @click="scrollToTop"
    >
      <template #icon>
        <icon-chevron-up />
      </template>
    </btn>
  </div>
</template>

<script>
import Btn from '~global/btn/btn.vue';
import IconChevronUp from '~icons/directional-navigation/chevron/up.svg';

import { mapState } from 'pinia';
import useIndexStore from '~stores/index';

/**
 * Vue: Scroll to top button (scroll-to-top-button)
 * -----------------------------------------------------------------------------
 * A arrow up button is shown, if a certain element or px bypassed and scrolls
 * the user to the top of the page on click.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
export default {
  name: 'ScrollToTopButton',
  components: { Btn, IconChevronUp },

  props: {
    // Threshold for the scroll position to show the button on mobile
    mobileThreshold: {
      type: Number,
      default: 768,
    },
    // Threshold for the scroll position to show the button on desktop
    desktopThreshold: {
      type: Number,
      default: 1024,
    },
  },

  data() {
    return {
      // Controls the visibility of the button
      showScrollButton: false,
      // Change this value to the pixel threshold
      scrollThreshold: 300,
      // To track if the intersection element is visible
      lastScrollY: 0,
      // Animation frame reference
      scrollAnimation: null,
      // Start time of animation
      animationStartTime: null,
      // Duration of animation in milliseconds
      animationDuration: 400,
      // Initial scroll position when animation starts
      initialScrollPosition: null,
      // check if add to cart is sticky
      isAddToCartSticky: false,
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
      isStickyVisible: (store) => store.stickyCta.visible,
    }),
  },

  mounted() {
    // Add scroll event listener when component is mounted
    window.addEventListener('scroll', this.checkScrollDirection);

    this.scrollThreshold = this.isMobile
      ? this.mobileThreshold
      : this.desktopThreshold;
  },

  beforeUnmount() {
    // Remove scroll event listener before component is unmounted
    window.removeEventListener('scroll', this.checkScrollDirection);
  },
  methods: {
    /**
     * Checks the scroll direction and updates the visibility of
     * the scroll-to-top button.
     */
    checkScrollDirection() {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > this.lastScrollY;

      this.showScrollButton =
        !scrollingDown && currentScrollY > this.scrollThreshold;

      this.lastScrollY = currentScrollY;
    },

    /**
     * Smoothly scrolls the window back to the top with an easing animation.
     */
    scrollToTop() {
      // Clear any existing animation frame
      cancelAnimationFrame(this.scrollAnimation);

      // Start animation
      this.animationStartTime = performance.now();
      this.animationStartScrollPosition = window.scrollY;
      this.scrollAnimation = requestAnimationFrame(this.animateScroll);
    },

    /**
     * Animation function for smooth scrolling.
     * @param {DOMHighResTimeStamp} currentTime - The current time when
     * the animation frame is fired.
     */
    animateScroll(currentTime) {
      const elapsed = currentTime - this.animationStartTime;

      // Calculate progress using easing function (easeInOutCubic)
      const progress = elapsed / this.animationDuration;
      const easedProgress = this.easeOutCirc(Math.min(progress, 1));

      // Calculate new scroll position
      const newScrollPosition =
        this.animationStartScrollPosition * (1 - easedProgress);

      // Apply new scroll position
      window.scrollTo(0, newScrollPosition);

      // Continue animation until duration is reached
      if (elapsed < this.animationDuration) {
        this.scrollAnimation = requestAnimationFrame(this.animateScroll);
      }
    },

    /**
     * Easing function for smooth scroll animation (circ ease-out).
     * @param {number} timeFactor - Time factor between 0 and 1.
     * @returns {number} - Eased value.
     */
    easeOutCirc(timeFactor) {
      const adjustedTime = timeFactor - 1;
      return Math.sqrt(1 - adjustedTime ** 2);
    },
  },
};
</script>

<style lang="scss">
@import './scroll-to-top-button';
</style>
